import React from 'react';
import {
    GameVariation,
    OnlineColorPreference,
    OnlineDirectInvite,
    OnlineLobbyPresenceUser,
    OnlineLobbyState,
    OnlineUser,
    PlayerPieces,
    PromotionRule,
} from '../types';

interface OnlineLobbyPanelProps {
    onlineUser: OnlineUser;
    lobbyState: OnlineLobbyState;
    selectedLobbyUserId: string | null;
    setSelectedLobbyUserId: (userId: string | null) => void;
    onlineBusy: boolean;
    onSendInvite: () => void;
    onAcceptInvite: (inviteId: string) => void;
    onRefuseInvite: (inviteId: string) => void;
    onCancelInvite: (inviteId: string) => void;
}

const formatPiece = (piece: PlayerPieces['white']): string => {
    switch (piece) {
        case 'Q':
            return 'Queen';
        case 'H':
            return 'Hawk';
        case 'E':
            return 'Elephant';
        case 'A':
            return 'Amazon';
        default:
            return '—';
    }
};

const formatVariation = (variation: GameVariation): string => {
    switch (variation) {
        case 'chess':
            return 'Chess';
        case 'chessx2':
            return 'Chessx2';
        case 'chessx8':
            return 'Chessx8';
        case 'chessx32':
            return 'Chessx32';
        case 'chess960':
            return 'Chess960';
        case 'chess960x2':
            return 'Chess960x2';
        case 'chess960x8':
            return 'Chess960x8';
        case 'chess960x32':
            return 'Chess960x32';
        case 'chess960blind':
            return 'Blind Chess960';
    }
};

const formatPromotionRule = (rule: PromotionRule): string => rule === 'full' ? 'Full promotion' : 'Limited promotion';

const formatColorPreference = (preference: OnlineColorPreference): string => {
    switch (preference) {
        case 'white':
            return 'Play as White';
        case 'black':
            return 'Play as Black';
        default:
            return 'Random color';
    }
};

const formatInviteStatus = (status: OnlineDirectInvite['status']): string => {
    switch (status) {
        case 'accepted':
            return 'Accepted';
        case 'refused':
            return 'Refused';
        case 'cancelled':
            return 'Cancelled';
        default:
            return 'Pending';
    }
};

/** One-line config summary; for Blind Chess960 the shared major piece is highlighted. */
const formatInviteConfigLine = (invite: OnlineDirectInvite): string => {
    if (invite.config.gameVariation === 'chess960blind') {
        const shared = formatPiece(invite.config.playerPieces.white ?? invite.config.playerPieces.black);
        return `Blind Chess960 — shared piece: ${shared} • ${formatPromotionRule(invite.config.promotionRule)} • ${formatColorPreference(invite.colorPreference)}`;
    }
    return `${formatVariation(invite.config.gameVariation)} • ${formatPiece(invite.config.playerPieces.white)} / ${formatPiece(invite.config.playerPieces.black)} • ${formatPromotionRule(invite.config.promotionRule)} • ${formatColorPreference(invite.colorPreference)}`;
};

const availabilityTone = (availability: OnlineLobbyPresenceUser['availability']): string => {
    switch (availability) {
        case 'available':
            return 'text-emerald-200 bg-emerald-500/10 border-emerald-400/30';
        case 'pending_invite':
            return 'text-cyan-200 bg-cyan-500/10 border-cyan-400/30';
        case 'in_game':
            return 'text-slate-200 bg-cyan-500/10 border-cyan-400/30';
    }
};

const AvailabilityBadge: React.FC<{ availability: OnlineLobbyPresenceUser['availability'] }> = ({ availability }) => (
    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${availabilityTone(availability)}`}>
        {availability.replace('_', ' ')}
    </span>
);

const UserAvatar: React.FC<{ name: string; avatarUrl?: string }> = ({ name, avatarUrl }) => {
    if (avatarUrl) {
        return <img src={avatarUrl} alt="" className="h-10 w-10 rounded-full border border-cyan-400/20 object-cover" />;
    }

    return (
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-cyan-400/20 bg-cyan-800/40 text-sm font-bold text-slate-200">
            {name.slice(0, 2).toUpperCase()}
        </div>
    );
};

export const OnlineLobbyPanel: React.FC<OnlineLobbyPanelProps> = ({
    onlineUser,
    lobbyState,
    selectedLobbyUserId,
    setSelectedLobbyUserId,
    onlineBusy,
    onSendInvite,
    onAcceptInvite,
    onRefuseInvite,
    onCancelInvite,
}) => {
    const availableUsers = lobbyState.users.filter((user) => user.id !== onlineUser.id && user.availability === 'available');
    const incomingInvites = lobbyState.incomingInvites.filter((invite) => invite.status === 'pending');
    const outgoingInvites = lobbyState.outgoingInvites;
    const pendingOutgoingInvite = outgoingInvites.find((invite) => invite.status === 'pending');
    const selfAvailability = lobbyState.self?.availability ?? 'available';
    const canSendInvite = Boolean(selectedLobbyUserId) && !pendingOutgoingInvite && !onlineBusy && selfAvailability === 'available';

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-cyan-400/20 bg-cyan-500/10 p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h4 className="text-sm font-semibold uppercase tracking-wider text-cyan-200">Lobby invite setup</h4>
                        <p className="mt-1 text-sm text-slate-300">Choose an opponent from the live lobby and send an invite using the settings selected above.</p>
                    </div>
                    <AvailabilityBadge availability={selfAvailability} />
                </div>

                {selfAvailability !== 'available' && (
                    <div className="mt-4 rounded-lg border border-cyan-400/30 bg-cyan-500/10 p-3 text-sm text-cyan-100">
                        {selfAvailability === 'in_game'
                            ? 'You are already in an active game, so you are not available for new invites.'
                            : 'You currently have a pending invite state. Resolve it before sending another invite.'}
                    </div>
                )}
            </div>

            <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
                <div className="space-y-4">
                    <div className="rounded-xl border border-cyan-400/10 bg-[#1e293b]/90 p-4">
                        <div className="mb-3 flex items-center justify-between gap-2">
                            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-200">Available players</h4>
                            <span className="text-xs text-slate-400">{availableUsers.length} online</span>
                        </div>
                        <div className="space-y-2">
                            {availableUsers.length === 0 && (
                                <div className="rounded-lg border border-cyan-400/8 bg-[#0f172a]/80 p-3 text-sm text-slate-400">
                                    No available players right now.
                                </div>
                            )}
                            {availableUsers.map((user) => {
                                const selected = selectedLobbyUserId === user.id;
                                return (
                                    <button
                                        key={user.id}
                                        onClick={() => setSelectedLobbyUserId(selected ? null : user.id)}
                                        disabled={Boolean(pendingOutgoingInvite)}
                                        className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${selected
                                            ? 'border-cyan-300/70 bg-cyan-500/15'
                                            : 'border-slate-700 bg-[#0f172a]/80 hover:bg-cyan-900/25'
                                            } disabled:opacity-60`}
                                    >
                                        <UserAvatar name={user.name} avatarUrl={user.avatarUrl} />
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm font-semibold text-white">{user.name}</div>
                                            <div className="text-xs text-slate-400">{user.provider}</div>
                                        </div>
                                        {selected && <span className="text-xs font-semibold text-cyan-200">Selected</span>}
                                    </button>
                                );
                            })}
                        </div>
                        <button
                            onClick={onSendInvite}
                            disabled={!canSendInvite}
                            className="mt-4 w-full rounded-xl bg-gradient-to-r from-cyan-600 via-cyan-600 to-cyan-700 px-4 py-3 text-sm font-bold text-white transition-all hover:from-cyan-500 hover:via-cyan-500 hover:to-cyan-600 disabled:opacity-50"
                        >
                            {onlineBusy ? 'Working…' : pendingOutgoingInvite ? 'Invite already pending' : 'Send Lobby Invite'}
                        </button>
                    </div>

                </div>

                <div className="space-y-4">
                    <div className="rounded-xl border border-cyan-400/10 bg-[#1e293b]/90 p-4">
                        <div className="mb-3 flex items-center justify-between gap-2">
                            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-200">Incoming invites</h4>
                            <span className="text-xs text-slate-400">{incomingInvites.length} pending</span>
                        </div>
                        <div className="space-y-3">
                            {incomingInvites.length === 0 && (
                                <div className="rounded-lg border border-cyan-400/8 bg-[#0f172a]/80 p-3 text-sm text-slate-400">
                                    No incoming invites right now.
                                </div>
                            )}
                            {incomingInvites.map((invite) => (
                                <div key={invite.id} className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 p-4">
                                    <div className="text-sm font-semibold text-white">{invite.inviter.name} invited you</div>
                                    <div className="mt-2 text-xs text-slate-200">
                                        {formatInviteConfigLine(invite)}
                                    </div>
                                    <div className="mt-3 flex gap-2">
                                        <button
                                            onClick={() => onAcceptInvite(invite.id)}
                                            disabled={onlineBusy}
                                            className="flex-1 rounded-lg border border-emerald-400/60 bg-emerald-500/20 px-3 py-2 text-sm font-medium text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-50"
                                        >
                                            Accept
                                        </button>
                                        <button
                                            onClick={() => onRefuseInvite(invite.id)}
                                            disabled={onlineBusy}
                                            className="flex-1 rounded-lg border border-rose-400/60 bg-rose-500/20 px-3 py-2 text-sm font-medium text-rose-100 hover:bg-rose-500/30 disabled:opacity-50"
                                        >
                                            Refuse
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-xl border border-cyan-400/10 bg-[#1e293b]/90 p-4">
                        <div className="mb-3 flex items-center justify-between gap-2">
                            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-200">Outgoing invites</h4>
                            <span className="text-xs text-slate-400">{outgoingInvites.length} total</span>
                        </div>
                        <div className="space-y-3">
                            {outgoingInvites.length === 0 && (
                                <div className="rounded-lg border border-cyan-400/8 bg-[#0f172a]/80 p-3 text-sm text-slate-400">
                                    You have not sent any invites yet.
                                </div>
                            )}
                            {outgoingInvites.map((invite) => (
                                <div key={invite.id} className="rounded-xl border border-cyan-400/8 bg-[#0f172a]/80 p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <div className="text-sm font-semibold text-white">To {invite.invitee.name}</div>
                                            <div className="mt-1 text-xs text-slate-400">
                                                {invite.config.gameVariation === 'chess960blind'
                                                    ? `Blind Chess960 (${formatPiece(invite.config.playerPieces.white ?? invite.config.playerPieces.black)}) • ${formatColorPreference(invite.colorPreference)}`
                                                    : `${formatVariation(invite.config.gameVariation)} • ${formatColorPreference(invite.colorPreference)}`}
                                            </div>
                                        </div>
                                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${invite.status === 'pending'
                                            ? 'bg-cyan-500/15 text-cyan-200'
                                            : invite.status === 'accepted'
                                                ? 'bg-emerald-500/15 text-emerald-200'
                                                : 'bg-cyan-500/15 text-cyan-200'
                                            }`}>
                                            {formatInviteStatus(invite.status)}
                                        </span>
                                    </div>
                                    {invite.status === 'accepted' && invite.roomId && (
                                        <div className="mt-2 text-xs text-emerald-200">
                                            Game is starting in room <span className="font-semibold text-emerald-100">{invite.roomId}</span>.
                                        </div>
                                    )}
                                    {invite.status === 'pending' && (
                                        <button
                                            onClick={() => onCancelInvite(invite.id)}
                                            disabled={onlineBusy}
                                            className="mt-3 rounded-lg border border-cyan-400/20 bg-[#0f172a]/80 px-3 py-2 text-sm font-medium text-slate-200 hover:bg-cyan-900/30 disabled:opacity-50"
                                        >
                                            Cancel invite
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
